// ========================================
// TWITTER/X OAUTH SERVICE
// ========================================
import { db, auth } from '../../firebase';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { oauthConfig, generateOAuthState } from '../../config/oauth.config';

export interface TwitterAuthResponse {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  error?: string;
}

export interface TwitterProfile {
  id: string;
  username: string;
  name: string;
  profileImageUrl?: string;
}

class TwitterOAuthService {
  private readonly config = oauthConfig.twitter;
  private codeVerifier: string = '';
  
  /**
   * Generate PKCE code verifier and challenge
   * Twitter OAuth 2.0 requires PKCE
   */
  private generatePKCE(): { verifier: string; challenge: string } {
    // Generate random code verifier (43-128 characters)
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const verifier = btoa(String.fromCharCode(...Array.from(array)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '')
      .substring(0, 128);
    
    // For Twitter, we use plain challenge (S256 requires server-side hashing)
    // In production, you should use S256 with proper hashing
    return { verifier, challenge: verifier };
  }
  
  /**
   * Step 1: Start OAuth flow - Redirect user to Twitter authorization
   */
  startOAuthFlow(): void {
    const state = generateOAuthState();
    const { verifier, challenge } = this.generatePKCE();
    
    console.log('🐦 Starting Twitter/X OAuth flow...');
    console.log('  - Generated state:', state);
    
    // Store state and PKCE verifier in sessionStorage
    sessionStorage.setItem('twitter_oauth_state', state);
    sessionStorage.setItem('twitter_oauth_timestamp', Date.now().toString());
    sessionStorage.setItem('twitter_code_verifier', verifier);
    
    // Build authorization URL with PKCE
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      state: state,
      scope: this.config.scopes.join(' '),
      code_challenge: challenge,
      code_challenge_method: 'plain' // Use 'S256' in production with proper hashing
    });
    
    const authUrl = `${this.config.authUrl}?${params.toString()}`;
    
    console.log('📍 Redirect URI:', this.config.redirectUri);
    console.log('🚀 Redirecting to Twitter...');
    
    // Redirect to Twitter
    window.location.href = authUrl;
  }
  
  /**
   * Step 2: Handle OAuth callback and exchange code for access token
   */
  async handleCallback(code: string, state: string): Promise<TwitterAuthResponse> {
    try {
      // Verify state to prevent CSRF attacks
      const storedState = sessionStorage.getItem('twitter_oauth_state');
      const timestamp = sessionStorage.getItem('twitter_oauth_timestamp');
      const codeVerifier = sessionStorage.getItem('twitter_code_verifier');
      
      console.log('🔐 State validation:');
      console.log('  - Received state:', state);
      console.log('  - Stored state:', storedState);
      console.log('  - Match:', storedState === state);
      
      if (!storedState) {
        console.warn('⚠️ WARNING: sessionStorage was cleared during OAuth redirect!');
      } else if (storedState !== state) {
        throw new Error('Invalid state parameter - mogelijk CSRF aanval');
      }
      
      // Check if state is not too old (max 10 minutes)
      if (timestamp) {
        const age = Date.now() - parseInt(timestamp);
        if (age > 10 * 60 * 1000) {
          throw new Error('OAuth state verlopen - probeer opnieuw');
        }
      }
      
      if (!codeVerifier) {
        throw new Error('PKCE code verifier niet gevonden');
      }
      
      // Clear state from storage
      sessionStorage.removeItem('twitter_oauth_state');
      sessionStorage.removeItem('twitter_oauth_timestamp');
      sessionStorage.removeItem('twitter_code_verifier');
      
      console.log('✅ State verified, exchanging code for token...');
      
      // Exchange authorization code for access token via Cloud Function
      const tokenResponse = await this.exchangeCodeForToken(code, codeVerifier);
      
      if (!tokenResponse.success || !tokenResponse.accessToken) {
        throw new Error(tokenResponse.error || 'Failed to get access token');
      }
      
      console.log('✅ Access token received');
      
      // Update connection status
      await this.updateConnectionStatus(true);
      
      console.log('✅ Twitter/X account successfully connected!');
      
      return tokenResponse;
      
    } catch (error) {
      console.error('❌ Twitter OAuth error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Exchange authorization code for access token
   * SECURE: Uses Firebase Cloud Function to keep client_secret on server
   */
  private async exchangeCodeForToken(code: string, codeVerifier: string): Promise<TwitterAuthResponse> {
    try {
      console.log('🔐 Exchanging code for token via Cloud Function...');
      
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const twitterTokenExchangeFn = httpsCallable(functions, 'twitterTokenExchange');
      
      const result = await twitterTokenExchangeFn({
        code: code,
        redirectUri: this.config.redirectUri,
        codeVerifier: codeVerifier
      });
      
      const data = result.data as any;
      
      if (!data.success) {
        throw new Error(data.error || 'Token exchange failed');
      }
      
      console.log('✅ Token exchange successful via Cloud Function');
      console.log('✅ Twitter profile fetched:', data.profile);
      
      return {
        success: true,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresIn: data.expiresIn
      };
      
    } catch (error) {
      console.error('❌ Token exchange error:', error);
      throw error;
    }
  }
  
  /**
   * Update connection status in Firestore
   */
  private async updateConnectionStatus(connected: boolean): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }
    
    const userId = auth.currentUser.uid;
    const now = new Date();
    
    try {
      const connectionRef = doc(db, 'socialConnections', userId);
      const connectionDoc = await getDoc(connectionRef);
      
      console.log('📝 Updating Twitter connection status. Connected:', connected);
      
      if (connectionDoc.exists()) {
        const updatedFields: any = {
          'twitter.connected': connected,
          'updatedAt': now
        };
        
        if (connected) {
          updatedFields['twitter.connectedAt'] = now;
        } else {
          updatedFields['twitter.disconnectedAt'] = now;
        }
        
        await updateDoc(connectionRef, updatedFields);
        console.log('✅ Connection status updated');
        
      } else {
        await setDoc(connectionRef, {
          uid: userId,
          linkedin: { connected: false },
          facebook: { connected: false },
          instagram: { connected: false },
          twitter: {
            connected: connected,
            connectedAt: connected ? now : undefined
          },
          pinterest: { connected: false },
          createdAt: now,
          updatedAt: now
        });
        console.log('✅ New connection document created');
      }
      
    } catch (error) {
      console.error('❌ Error updating connection status:', error);
      throw error;
    }
  }
  
  /**
   * Post content to Twitter/X
   * SECURE: Uses Cloud Function with backend-stored tokens
   */
  async postToTwitter(
    content: string,
    userId: string,
    imageUrls?: string[]
  ): Promise<{ success: boolean; tweetUrl?: string; error?: string }> {
    try {
      console.log('🐦 Posting to Twitter/X...');
      
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const postToTwitterFn = httpsCallable(functions, 'postToTwitter');
      
      const result = await postToTwitterFn({
        content: content,
        imageUrls: imageUrls || []
      });
      
      const data = result.data as {
        success: boolean;
        tweetUrl?: string;
        tweetId?: string;
        error?: string;
      };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to post to Twitter');
      }
      
      console.log('✅ Posted to Twitter successfully:', data.tweetUrl);
      
      return {
        success: true,
        tweetUrl: data.tweetUrl
      };
      
    } catch (error) {
      console.error('❌ Twitter posting error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Disconnect Twitter account
   */
  async disconnect(userId: string): Promise<void> {
    console.log('🔌 Twitter disconnect starting for userId:', userId);
    
    try {
      const connectionRef = doc(db, 'socialConnections', userId);
      
      await updateDoc(connectionRef, {
        'twitter.connected': false,
        'twitter.disconnectedAt': new Date(),
        'twitter.accessToken': null,
        'twitter.refreshToken': null
      });
      
      console.log('✅ Twitter disconnected from Firestore');
      
      // Optional: Revoke tokens via Cloud Function
      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const revokeTokens = httpsCallable(functions, 'revokeTwitterTokens');
        await revokeTokens({});
        console.log('✅ Twitter tokens revoked via Cloud Function');
      } catch (fnError) {
        console.log('ℹ️ Cloud Function not available (this is OK):', fnError);
      }
      
    } catch (error) {
      console.error('❌ Error disconnecting Twitter:', error);
      throw error;
    }
  }
  
  /**
   * Check if Twitter is connected
   */
  async isConnected(userId: string): Promise<boolean> {
    try {
      const connectionRef = doc(db, 'socialConnections', userId);
      const connectionDoc = await getDoc(connectionRef);
      
      if (!connectionDoc.exists()) return false;
      
      const twitterData = connectionDoc.data().twitter;
      return twitterData?.connected === true;
      
    } catch (error) {
      console.error('Error checking Twitter connection:', error);
      return false;
    }
  }
}

export const twitterOAuth = new TwitterOAuthService();
